import * as React from 'react';
import { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Button,
  Container,
  Paper,
  InputBase,
  IconButton,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Slider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

function truncateText(text, maxLength) {
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
}

function getMediaType(x) {
  if (x === 'tv') {
    return 'TV Series';
  }
  if (x === 'movie') {
    return 'Movie';
  }

  return 'Misc. Media';
}

function getReleaseDate(x) {
  if (typeof x === 'undefined') {
    return 'Year Unknown';
  }
  return x.split('-')[0];
}

function getStatus(movie) {
  if (movie.ari_approved) return "Ari's List";
  if (movie.approved) return 'Approved';
  return 'Pending Review';
}

function getTypeChipStyles(type) {
  if (type === 'Movie') {
    return {
      backgroundColor: '#e3f2fd',
      color: '#1565c0',
      border: '1px solid #bbdefb',
    };
  }

  if (type === 'TV Series') {
    return {
      backgroundColor: '#e8f5e9',
      color: '#2e7d32',
      border: '1px solid #c8e6c9',
    };
  }

  return {
    backgroundColor: '#f5f5f5',
    color: '#616161',
    border: '1px solid #e0e0e0',
  };
}

function getStatusChipStyles(status) {
  if (status === 'Approved' || status === "Ari's List") {
    return {
      backgroundColor: '#e8f5e9',
      color: '#2e7d32',
      border: '1px solid #c8e6c9',
    };
  }

  return {
    backgroundColor: '#ffebee',
    color: '#c62828',
    border: '1px solid #ffcdd2',
  };
}

function MediaMetaChips({ movie, hideStatus = false }) {
  const mediaType = getMediaType(movie.media_type);
  const year = getReleaseDate(movie.release_date || movie.first_air_date);
  const status = getStatus(movie);

  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.25 }}>
      <Chip
        label={mediaType}
        size="small"
        sx={{
          borderRadius: '999px',
          fontWeight: 600,
          ...getTypeChipStyles(mediaType),
        }}
      />
      <Chip
        label={year}
        size="small"
        sx={{
          borderRadius: '999px',
          fontWeight: 600,
          backgroundColor: '#f8f9fa',
          color: '#424242',
          border: '1px solid #e0e0e0',
        }}
      />
      {!hideStatus ? (
        <Chip
          label={status}
          size="small"
          sx={{
            borderRadius: '999px',
            fontWeight: 600,
            ...getStatusChipStyles(status),
          }}
        />
      ) : null}
    </Box>
  );
}

function FilterToggleChip({ label, active, onClick, activeStyles }) {
  return (
    <Chip
      label={label}
      clickable
      onClick={onClick}
      size="small"
      sx={{
        borderRadius: '999px',
        fontWeight: 600,
        transition: 'all 150ms ease',
        border: '1px solid #e0e0e0',
        backgroundColor: active ? activeStyles.backgroundColor : '#f5f5f5',
        color: active ? activeStyles.color : '#757575',
        '&:hover': {
          backgroundColor: active ? activeStyles.backgroundColor : '#eeeeee',
        },
      }}
    />
  );
}

function MovieCard({ movie, children, sx = {}, ...props }) {
  return (
    <Card
      {...props}
      sx={{
        height: '100%',
        borderRadius: 3,
        boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
        border: '1px solid #ededed',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {movie.poster_path ? (
        <CardMedia
          component="img"
          height="180"
          image={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
          alt={movie.title || movie.name}
        />
      ) : (
        <Box
          sx={{
            height: '180px',
            backgroundColor: '#111',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          No Image Available
        </Box>
      )}
      {children}
    </Card>
  );
}

const Suggest = ({ addToList, myList, isListLoading = false }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [clickedCard, setClickedCard] = useState(null);
  const [movieToConfirm, setMovieToConfirm] = useState(null);
  const [searchAttempted, setSearchAttempted] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const availableYears = useMemo(() => {
    const years = myList
      .map((movie) => parseInt(getReleaseDate(movie.release_date || movie.first_air_date), 10))
      .filter((year) => !Number.isNaN(year));

    if (years.length === 0) {
      const currentYear = new Date().getFullYear();
      return { min: currentYear - 30, max: currentYear, values: [currentYear - 30, currentYear] };
    }

    const min = Math.min(...years);
    const max = Math.max(...years);

    return { min, max, values: [min, max] };
  }, [myList]);

  const [yearRange, setYearRange] = useState(availableYears.values);

  const availableYearValues = availableYears.values;

  React.useEffect(() => {
    setYearRange(availableYearValues);
  }, [availableYearValues]);

  const toggleType = (type) => {
    setSelectedTypes((prev) => {
      if (prev.includes(type)) {
        return prev.filter((t) => t !== type);
      }
      return [...prev, type];
    });
  };

  const handleClick = (movie) => {
    setClickedCard(movie.id);
    addToList(movie);
    setTimeout(() => setClickedCard(null), 80);
    setQuery('');
    setResults([]);
    setMovieToConfirm(null);
    setSearchAttempted(false);
  };

  const searchMovies = async (e) => {
    e.preventDefault();

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setResults([]);
      setSearchAttempted(false);
      return;
    }

    setIsLoading(true);
    setSearchAttempted(false);
    setMovieToConfirm(null);

    const url = `https://eliteari.pythonanywhere.com/search_movies?query=${encodeURIComponent(trimmedQuery)}`;

    try {
      const response = await axios.get(url);
      setResults(response.data.results.slice(0, 6));
    } catch (error) {
      console.error('Error fetching data:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
      setSearchAttempted(true);
    }
  };

  const pendingMovies = useMemo(
    () => myList.slice().reverse().filter((movie) => !movie.approved),
    [myList]
  );

  const approvedMovies = useMemo(
    () => myList.slice().reverse().filter((movie) => movie.approved && !movie.ari_approved),
    [myList]
  );

  const personalMovies = useMemo(
    () => myList.slice().reverse().filter((movie) => movie.ari_approved),
    [myList]
  );

  const filteredTabMovies = useMemo(() => {
    const tabMovies =
      activeTab === 0
        ? pendingMovies
        : activeTab === 1
        ? approvedMovies
        : personalMovies;
  
    return tabMovies.filter((movie) => {
      const year = parseInt(
        getReleaseDate(movie.release_date || movie.first_air_date),
        10
      );
  
      const matchesType =
        selectedTypes.length === 0 ||
        selectedTypes.length === 2 ||
        selectedTypes.includes(movie.media_type) ||
        !['movie', 'tv'].includes(movie.media_type);
  
      const matchesYear =
        Number.isNaN(year) || (year >= yearRange[0] && year <= yearRange[1]);
  
      return matchesType && matchesYear;
    });
  }, [activeTab, pendingMovies, approvedMovies, personalMovies, selectedTypes, yearRange]);

  return (
    <Container sx={{ width: '100%', py: 2.5 }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ mb: 2, textAlign: 'center', fontWeight: 700 }}
      >
        Suggest things for me to watch!
      </Typography>

      <form onSubmit={searchMovies}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Paper
            component="div"
            sx={{
              p: '4px 6px',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: 520,
              borderRadius: 999,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid #eaeaea',
            }}
          >
            <InputBase
              sx={{ ml: 1.5, flex: 1 }}
              placeholder="Search for a movie, anime, TV show..."
              inputProps={{ 'aria-label': 'search media suggestions' }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            {query ? (
              <IconButton
                type="button"
                onClick={() => {
                  setQuery('');
                  setResults([]);
                  setSearchAttempted(false);
                  setIsLoading(false);
                }}
                aria-label="clear search"
              >
                <CloseIcon />
              </IconButton>
            ) : null}

            <IconButton
              type="submit"
              aria-label="search"
              disabled={isLoading}
              sx={{ ml: 0.5 }}
            >
              <SearchIcon />
            </IconButton>
          </Paper>
        </Box>
      </form>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 1.5, color: 'text.secondary' }}>
            Searching...
          </Typography>
        </Box>
      ) : results.length === 0 && query.trim() !== '' && searchAttempted === true ? (
        <Typography variant="subtitle1" sx={{ textAlign: 'center', mt: 2.5 }}>
          No results found. Try another search!
        </Typography>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {results.map((movie) => (
            <Grid item key={movie.id} xs={12} sm={6} md={4}>
              <MovieCard
                movie={movie}
                onClick={() => setMovieToConfirm(movie)}
                onMouseEnter={() => setHoveredCard(movie.id)}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: hoveredCard === movie.id ? '#fafafa' : '#fff',
                  transform: clickedCard === movie.id ? 'scale(0.98)' : 'scale(1)',
                  transition:
                    'transform 150ms ease-in-out, background-color 150ms ease-in-out, box-shadow 150ms ease-in-out',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  },
                }}
              >
                {movieToConfirm && movieToConfirm.id === movie.id ? (
                  <Container sx={{ py: 3, textAlign: 'center' }}>
                    <Typography sx={{ mb: 1.5 }}>
                      Are you sure you want to suggest: <strong>{movie.name || movie.title}</strong>?
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick(movie);
                        }}
                      >
                        <strong>Yes</strong>
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMovieToConfirm(null);
                        }}
                      >
                        <strong>No</strong>
                      </Button>
                    </Box>
                  </Container>
                ) : (
                  <CardContent>
                    <Typography variant="h6" component="div">
                      {truncateText(movie.title || movie.name, 25)}
                    </Typography>
                    <MediaMetaChips movie={movie} hideStatus />
                  </CardContent>
                )}
              </MovieCard>
            </Grid>
          ))}
        </Grid>
      )}

      <Box
        sx={{
          mt: 0.5,
          mb: 2,
          px: 1.5,
          py: 1.25,
          borderRadius: 3,
          border: '1px solid #ececec',
          backgroundColor: '#fafafa',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Filter:
            </Typography>
        
            <FilterToggleChip
              label="Movie"
              active={selectedTypes.includes('movie')}
              onClick={() => toggleType('movie')}
              activeStyles={{
                backgroundColor: '#e3f2fd',
                color: '#1565c0',
              }}
            />
        
            <FilterToggleChip
              label="TV Series"
              active={selectedTypes.includes('tv')}
              onClick={() => toggleType('tv')}
              activeStyles={{
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
              }}
            />
          </Box>
          <Box
            sx={{
              width: '100%',
              maxWidth: 320,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {/* Min year */}
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', minWidth: 32, textAlign: 'right' }}
            >
              {yearRange[0]}
            </Typography>
          
            {/* Slider */}
            <Slider
              value={yearRange}
              onChange={(event, newValue) => setYearRange(newValue)}
              valueLabelDisplay="auto"
              min={availableYears.min}
              max={availableYears.max}
              disableSwap
              size="small"
              sx={{ flex: 1 }}
            />
          
            {/* Max year */}
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', minWidth: 32 }}
            >
              {yearRange[1]}
            </Typography>
          </Box>
 
        </Box>
        
      </Box>

      <Box
        sx={{
          borderRadius: 3,
          border: '1px solid #ececec',
          overflow: 'hidden',
          backgroundColor: '#fff',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            borderBottom: '1px solid #ececec',
            '& .MuiTab-root': {
              fontWeight: 600,
              minHeight: 56,
              textTransform: 'none',
            },
          }}
        >
          <Tab label={`Pending (${pendingMovies.length})`} />
          <Tab label={`Approved (${approvedMovies.length})`} />
          <Tab label={`Ari's List (${personalMovies.length})`} />
        </Tabs>
      
        <Box sx={{ p: 2, minHeight: 220 }}>
          {activeTab === 2 && (
            <Typography
              sx={{
                fontStyle: 'italic',
                mb: 2,
                color: '#616161',
              }}
            >
              These items are on Ari's personal list of stuff he wants to watch.
            </Typography>
          )}
          {isListLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                Loading items...
              </Typography>
            </Box>
          ) : filteredTabMovies.length === 0 ? (
            <Typography variant="subtitle1" sx={{ textAlign: 'center', py: 3 }}>
              {activeTab === 0
                ? 'No Pending Suggestions.'
                : activeTab === 1
                ? 'No Approved Suggestions.'
                : "Nothing in Ari's List."}
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {filteredTabMovies.map((movie) => (
                <Grid item key={movie.id} xs={12} sm={6} md={4}>
                  <MovieCard movie={movie}>
                    <CardContent>
                      <Typography variant="h6" component="div">
                        {truncateText(movie.title || movie.name, 25)}
                      </Typography>
                      <MediaMetaChips movie={movie} />
                    </CardContent>
                  </MovieCard>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default Suggest;
